import { useEffect, useState } from 'react';
import { BellRing, ChevronDown, ChevronRight, ListChecks, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { SOWIntegrationSettings } from './SOWIntegrationSettings';
import { normalizeStatus } from '../utils/configItemLifecycle';

/**
 * The SOW modal's Team, Config items and Integrations sections (RESTORE_PLAN
 * R3.5, carrying the retired SOWDetailView's Team tab, its config-item stat
 * and its Integrations tab): who works on the engagement, how many of its
 * configuration items are approved, and the per-SOW Slack and Microsoft Teams
 * notifications (the kept SOWIntegrationSettings, with NotificationHelpDialog),
 * which the server still sends.
 */

export interface SowPeople {
  id: string;
  name?: string;
  companyId?: string;
  engagementLeadId?: string;
  engagementLeadName?: string;
  ownerName?: string;
  teamMembers?: string[];
  assignedConsultants?: string[];
}

/** Items in the SOW's scope: those tagged with it, else (items made before tagging) the company's. */
export function configItemProgress(sowId: string, items: any[]): { done: number; total: number } {
  const list = (Array.isArray(items) ? items : []).filter((i) => i && typeof i === 'object');
  const tagged = list.filter((i) => i.engagementId === sowId);
  const scope = tagged.length ? tagged : list.filter((i) => !i.engagementId);
  return { done: scope.filter((i) => normalizeStatus(i) === 'approved').length, total: scope.length };
}

/** The consultants on the engagement: the lead, the assigned firm users by name, and the names typed on the SOW. */
export function sowTeam(sow: SowPeople, firmUsers: Array<{ id: string; name?: string; email?: string }>): { lead: string | null; consultants: string[]; named: string[] } {
  const nameOf = (id: string) => {
    const u = firmUsers.find((x) => x.id === id);
    return u ? (u.name || u.email || id) : null;
  };
  const lead = sow.engagementLeadName || (sow.engagementLeadId ? nameOf(sow.engagementLeadId) : null) || sow.ownerName || null;
  const consultants = [...new Set((sow.assignedConsultants ?? []).map(nameOf).filter((n): n is string => !!n && n !== lead))];
  const named = [...new Set((sow.teamMembers ?? []).filter((n) => n && n !== lead && !consultants.includes(n)))];
  return { lead, consultants, named };
}

export function SOWTeamAndIntegrations({ sow, firmUsers, accessToken, projectId, currentUser }: {
  sow: SowPeople;
  firmUsers: Array<{ id: string; name?: string; email?: string }>;
  accessToken: string;
  projectId: string;
  currentUser?: any;
}) {
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [integrationsOpen, setIntegrationsOpen] = useState(false);

  useEffect(() => {
    let live = true;
    if (!sow.companyId) return;
    fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${sow.companyId}/config-items`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (live && b) setProgress(configItemProgress(sow.id, b.data ?? b.configItems ?? [])); })
      .catch(() => {});
    return () => { live = false; };
  }, [sow.id, sow.companyId, accessToken, projectId]);

  const team = sowTeam(sow, firmUsers);
  const pct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-lg border p-4" data-testid="sow-team-section">
          <h4 className="flex items-center gap-2 mb-3"><Users className="size-4 text-muted-foreground" aria-hidden="true" />Team</h4>
          <dl className="space-y-2">
            <div><dt className="text-muted-foreground">Engagement lead</dt><dd>{team.lead || 'Not set'}</dd></div>
            <div><dt className="text-muted-foreground">Consultants</dt><dd>{team.consultants.length ? team.consultants.join(', ') : '—'}</dd></div>
            {team.named.length > 0 && <div><dt className="text-muted-foreground">Also on the SOW</dt><dd>{team.named.join(', ')}</dd></div>}
          </dl>
        </div>
        <div className="rounded-lg border p-4" data-testid="sow-config-items">
          <h4 className="flex items-center gap-2 mb-3"><ListChecks className="size-4 text-muted-foreground" aria-hidden="true" />Config items</h4>
          {progress === null ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : progress.total === 0 ? (
            <p className="text-muted-foreground">No configuration items in scope yet.</p>
          ) : (
            <>
              <p>{progress.done} of {progress.total} approved</p>
              <Progress value={pct} className="h-2 mt-2" />
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border" data-testid="sow-integrations">
        <Button
          variant="ghost"
          className="w-full justify-start"
          aria-expanded={integrationsOpen}
          onClick={() => setIntegrationsOpen(!integrationsOpen)}
        >
          {integrationsOpen ? <ChevronDown className="size-4 mr-2" aria-hidden="true" /> : <ChevronRight className="size-4 mr-2" aria-hidden="true" />}
          <BellRing className="size-4 mr-2" aria-hidden="true" />
          Slack and Teams notifications
        </Button>
        {integrationsOpen && (
          <div className="p-4 pt-0">
            <SOWIntegrationSettings sowId={sow.id} sowName={sow.name || 'this SOW'} accessToken={accessToken} currentUser={currentUser ?? {}} />
          </div>
        )}
      </div>
    </div>
  );
}
